import { useState } from 'react';
import { normalizeImageUrl } from '../utils/utils';
import { instance } from '../api/api';

/**
 * Хук для управления поворотом изображений
 * @param onSaveSuccess - Функция, вызываемая после успешного сохранения
 * @returns Объект с состоянием и методами для управления поворотом
 */
export const useImageRotation = (onSaveSuccess?: () => void) => {
  const [rotation, setRotation] = useState<number>(0);
  const [isRotating, setIsRotating] = useState<boolean>(false);

  /**
   * Поворачивает изображение
   * @param direction Направление поворота ('left' или 'right')
   * @param imgUrl URL изображения
   * @param photoType Тип фото (для сохранения)
   * @param entityId ID сущности (автомобиля, пользователя и т.д.)
   * @param saveEndpoint Эндпоинт для сохранения файла
   */
  const rotateImage = async (
    direction: 'left' | 'right',
    imgUrl: string,
    photoType: string,
    entityId: string,
    saveEndpoint: string,
  ) => {
    if (isRotating) return;

    setIsRotating(true);
    const newRotation = direction === 'left' ? rotation - 90 : rotation + 90;
    setRotation(newRotation);

    // Даем время для обновления DOM перед созданием канваса
    setTimeout(() => {
      saveRotatedImage(imgUrl, photoType, newRotation, entityId, saveEndpoint);
    }, 100);
  };

  /**
   * Сохраняет повернутое изображение на сервере
   */
  const saveRotatedImage = async (
    imgUrl: string,
    photoType: string,
    angle: number,
    entityId: string,
    saveEndpoint: string,
  ) => {
    try {
      // Загружаем изображение через прокси
      const img = new Image();
      // The image is drawn onto a canvas and re-uploaded, so it has to be a
      // CORS-clean load: a presigned S3 URL is cross-origin, and without this
      // the canvas is tainted and toBlob() throws. The API's /uploads mount
      // already answers with Access-Control-Allow-Origin: *.
      img.crossOrigin = 'anonymous';
      const imgPath = normalizeImageUrl(imgUrl);

      // Ждём загрузки изображения
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imgPath;
      });

      // Создаем канвас для рисования повернутого изображения
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setIsRotating(false);
        return;
      }

      // Нормализуем угол
      const normalizedAngle = ((angle % 360) + 360) % 360;

      // Устанавливаем размеры канваса
      if (normalizedAngle % 180 === 0) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      } else {
        canvas.width = img.naturalHeight;
        canvas.height = img.naturalWidth;
      }

      // Рисуем повернутое изображение
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((normalizedAngle * Math.PI) / 180);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);

      // Конвертируем канвас в Blob, затем в File
      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], `rotated_${photoType}.jpg`, {
            type: 'image/jpeg',
          });

          // Отправляем файл на сервер
          await saveFile(file, photoType, entityId, saveEndpoint);
          if (onSaveSuccess) onSaveSuccess();
        }
        setIsRotating(false);
      }, 'image/jpeg');
    } catch (error) {
      console.error('Ошибка при сохранении повернутого изображения:', error);
      setIsRotating(false);
    }
  };

  /**
   * Отправляет файл на сервер
   */
  const saveFile = async (file: File, photoType: string, entityId: string, endpoint: string) => {
    try {
      const formData = new FormData();

      // Для аватаров пользователей используем специальный endpoint и имя поля
      if (photoType === 'avatar') {
        formData.append('avatar', file);
        await instance.post(`${endpoint}/avatar/${entityId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Для остальных типов фото используем стандартный API
        formData.append(photoType, file);
        await instance.put(`${endpoint}/${entityId}/photos`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
    } catch (error) {
      console.error('Ошибка при обновлении фото:', error);
    }
  };

  return {
    rotation,
    isRotating,
    rotateImage,
    setRotation,
  };
};
